import React from 'react';
import Select from 'react-select';

import selectStyles from './selectStyles';

/**
 * react-select, configured once for this page.
 *
 * **The class prefix is deliberately not `cm_select`.** `main.css:330` styles that prefix globally
 * with `border: 0 !important; background: transparent !important; top: -5px !important`, written
 * for the boxes the rest of the app draws. `!important` outranks the emotion classes the `styles`
 * prop generates, so a `cm_select` control on this page renders borderless and 5px high no matter
 * what the style object says. A private prefix is what lets `selectStyles` describe the control
 * outright, with no `!important` and no reach into the rest of the app. `ProgramForm/ui/Inputs.js`
 * owns `pf_select` for exactly this reason.
 *
 * Menus render in a portal at `document.body`: the modal's form body scrolls, and a menu opened
 * near the bottom of it was clipped. Portalling sidesteps the containment question rather than
 * trying to keep every ancestor unclipped.
 */
const SELECT_PREFIX = 'cpn_select';

/**
 * Close an open menu when anything other than the menu itself scrolls.
 *
 * Has to be the function form. As a boolean, react-select only closes when the scroll target is
 * the documentElement, body or window — and this form scrolls inside the modal's own body, which
 * is none of those, so a portalled menu would hang in place while the fields moved under it. The
 * menu's own list is exempt, or scrolling a long option list would dismiss it.
 */
const closeOnOutsideScroll = event => {
    const { target } = event;
    if (!(target instanceof HTMLElement)) return true;
    return !target.closest(`.${SELECT_PREFIX}__menu, .${SELECT_PREFIX}__menu-portal`);
};

const toBody = () => document.body;

const CouponSelect = props => (
    <Select
        classNamePrefix={SELECT_PREFIX}
        styles={selectStyles}
        menuPortalTarget={toBody()}
        closeMenuOnScroll={closeOnOutsideScroll}
        // Defaults to true, and fights `closeMenuOnScroll` above: opening a menu near the bottom
        // of the modal's own scrolling body makes react-select scroll that body to fit the menu
        // on screen, and that self-inflicted scroll is exactly what `closeOnOutsideScroll` reads
        // as "the page moved out from under the menu" — closing it again immediately, which reads
        // as the dropdown flickering open/closed and the page jumping. Unnecessary here anyway:
        // the menu is portalled to document.body and positions itself against the viewport, so it
        // never actually needs its anchor scrolled into view to render correctly.
        menuShouldScrollIntoView={false}
        {...props}
    />
);

export default CouponSelect;
